import { Link } from "react-router-dom";
import { TrendUp, ArrowLeft } from "@phosphor-icons/react";

const EFFECTIVE = "June 2, 2026";
const CONTACT = "support@marketpilot365.com";

const DOCS = {
  privacy: {
    title: "Privacy Policy",
    intro: "This Privacy Policy explains what information MarketPilot AI (\"MarketPilot\", \"we\", \"us\") collects, how we use it, and the choices you have. By using the platform you agree to this policy.",
    sections: [
      ["Information we collect", [
        "Account data: your name, email address, and a securely hashed password when you register.",
        "Usage data: pages viewed, watchlists, saved scans, alerts, and paper-trading activity you create in the app.",
        "Brokerage credentials (optional): if you connect a broker (Alpaca or OANDA), your API keys/tokens are encrypted at rest and are never displayed back to you or shared. We use them only to place and manage the orders you request.",
        "Payment data: subscription billing is handled by Stripe. We do not store your full card details on our servers.",
        "Messaging (optional): if you link Telegram for alerts, we store the chat identifier needed to deliver your notifications.",
      ]],
      ["How we use your information", [
        "To provide and operate the platform: dashboards, scanners, AI scoring, forecasts, paper trading, and (if connected) broker order routing.",
        "To deliver alerts, daily recaps, and weekly digests you opt into.",
        "To manage subscriptions and process payments via Stripe.",
        "To secure the service, prevent abuse, and comply with legal obligations.",
      ]],
      ["Market & third-party data", [
        "Market data is sourced from third-party providers and may be delayed. Broker connectivity is provided by Alpaca and OANDA under their own terms and privacy policies.",
        "AI features are powered by third-party large-language-model providers; prompts you submit to the assistant may be processed by those providers to generate responses.",
      ]],
      ["Data retention & security", [
        "We retain account and activity data for as long as your account is active. You may request deletion at any time.",
        "Sensitive credentials are encrypted at rest. No method of transmission or storage is 100% secure, and we cannot guarantee absolute security.",
      ]],
      ["Your choices", [
        "You can update your profile, change your password, unlink brokers or Telegram, and disable recaps/digests from within the app.",
        "You can request access to, correction of, or deletion of your personal data by contacting us.",
      ]],
      ["Cookies & local storage", [
        "We use only essential cookies and browser local storage to keep you signed in (session token) and remember in-app preferences. We do not use third-party advertising or cross-site tracking cookies.",
        "Our payment provider (Stripe) may set cookies needed to process payments securely. You can clear cookies in your browser at any time, though doing so will sign you out.",
      ]],
      ["Contact us", [
        `For any privacy request — access, correction, or deletion of your data — email ${CONTACT} and we'll respond promptly.`,
      ]],
    ],
  },
  terms: {
    title: "Terms of Service",
    intro: "These Terms of Service (\"Terms\") govern your use of MarketPilot AI. By creating an account or using the platform, you agree to these Terms.",
    sections: [
      ["The service", [
        "MarketPilot is a market-intelligence and analytics platform covering U.S. stocks, crypto, and forex. It provides AI scoring, probabilistic forecasts, scanners, a paper-trading simulator, and optional connectivity to third-party brokers (Alpaca, OANDA) that you control.",
        "MarketPilot is an information and software tool only. It is not a broker-dealer, investment adviser, or fiduciary.",
      ]],
      ["Not financial advice", [
        "Nothing on the platform constitutes financial, investment, tax, or legal advice, or a recommendation to buy or sell any security, cryptocurrency, or currency pair. All scores, forecasts, and AI outputs are probabilities and model interpretations — never guarantees. You are solely responsible for your own decisions.",
      ]],
      ["Accounts & eligibility", [
        "You must provide accurate information, keep your credentials secure, and be legally able to enter these Terms in your jurisdiction. You are responsible for all activity under your account.",
      ]],
      ["Brokerage connections & live trading", [
        "Connecting a live brokerage account and placing real orders involves real money and real risk. You authorize MarketPilot to transmit only the orders you explicitly submit. You remain responsible for reviewing and confirming every live order.",
        "We are not responsible for broker outages, rejected orders, market halts, slippage, or losses arising from live or paper trading.",
      ]],
      ["Subscriptions", [
        "Paid tiers (Pro, Elite) are billed through Stripe on a recurring basis until cancelled. Features vary by tier. Prices may change with notice.",
      ]],
      ["Acceptable use", [
        "Do not misuse the service, attempt to breach security, scrape at scale, resell access, or use the platform for unlawful activity.",
      ]],
      ["Disclaimers & limitation of liability", [
        "The service is provided \"as is\" without warranties of any kind. To the maximum extent permitted by law, MarketPilot is not liable for any indirect, incidental, or consequential damages, or for any trading losses.",
      ]],
      ["Changes & termination", [
        "We may update these Terms or the service at any time. We may suspend or terminate accounts that violate these Terms. Continued use after changes constitutes acceptance.",
      ]],
      ["Contact", [`Questions about these Terms? Email ${CONTACT}.`]],
    ],
  },
  risk: {
    title: "Risk Disclaimer",
    intro: "Trading and investing carry substantial risk. Please read this disclaimer carefully before using MarketPilot AI or acting on any information it provides.",
    sections: [
      ["General risk", [
        "Trading stocks, cryptocurrencies, and foreign exchange (forex) involves a high level of risk and may not be suitable for all investors. You can lose some or all of your capital. Never trade with money you cannot afford to lose.",
      ]],
      ["Probabilities, not guarantees", [
        "Every AI score, signal, confidence level, and forecast on the platform is a statistical probability or model interpretation based on historical and current data. It is not a prediction, promise, or guarantee of future performance. Past performance does not indicate future results.",
      ]],
      ["Forex & leverage", [
        "Forex trading is conducted with leverage, which magnifies both gains and losses. A small adverse move can result in losses exceeding your initial margin. Understand your broker's margin and leverage terms fully before trading.",
      ]],
      ["Crypto volatility", [
        "Cryptocurrency markets are highly volatile, trade 24/7, and may be subject to unique liquidity and regulatory risks.",
      ]],
      ["Paper vs. live", [
        "The paper-trading simulator uses no real money and is for education and practice only. Simulated results do not reflect real fills, slippage, fees, or the emotional realities of live trading. Switching to a live broker connection uses real funds and real risk.",
      ]],
      ["No advice; do your own research", [
        "MarketPilot is not a broker-dealer or investment adviser and does not provide personalized financial advice. Consult a licensed professional before making investment decisions. You are solely responsible for your trades.",
      ]],
      ["Contact", [`Questions? Email ${CONTACT}.`]],
    ],
  },
};

export default function Policy({ doc = "privacy" }) {
  const d = DOCS[doc] || DOCS.privacy;
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid={`policy-${doc}`}>
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/login" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center bg-volt rounded-sm"><TrendUp size={18} weight="bold" className="text-white" /></div>
            <span className="font-heading font-bold">MarketPilot<span className="text-volt"> AI</span></span>
          </Link>
          <Link to="/login" data-testid="policy-back" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-white transition-colors duration-200"><ArrowLeft size={15} /> Back to home</Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="font-heading text-4xl font-bold tracking-tight">{d.title}</h1>
        <p className="mono text-[11px] uppercase tracking-widest text-muted-foreground mt-2">Effective {EFFECTIVE}</p>
        <p className="mt-6 text-muted-foreground leading-relaxed">{d.intro}</p>

        <div className="mt-10 space-y-9">
          {d.sections.map(([heading, items], i) => (
            <section key={i}>
              <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
                <span className="mono text-volt text-sm">{String(i + 1).padStart(2, "0")}</span> {heading}
              </h2>
              <ul className="mt-3 space-y-2.5">
                {items.map((t, j) => (
                  <li key={j} className="flex gap-2.5 text-sm text-muted-foreground leading-relaxed">
                    <span className="text-volt mt-1.5 shrink-0 w-1 h-1 rounded-full bg-volt" /> <span>{t}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <div className="mt-14 pt-6 border-t border-border flex flex-wrap gap-x-6 gap-y-2 mono text-xs text-muted-foreground">
          <Link to="/privacy" className="hover:text-white">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-white">Terms of Service</Link>
          <Link to="/risk" className="hover:text-white">Risk Disclaimer</Link>
          <span className="ml-auto">© {new Date().getFullYear()} MarketPilot AI</span>
        </div>
      </main>
    </div>
  );
}
